import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { MoreVertical, Pencil, Trash2, Copy, Info, FileText, Download, Check, X, Eye, EyeOff } from "lucide-react";
import MessageTicks from "./MessageTicks";
import Checkbox from "../common/Checkbox";
import { parseCustomMessage } from "../../utils/chatHelpers";
import { color, radius } from "../../styles/theme";

/**
 * One chat message bubble. Ported visually from mobile's
 * `bubble-chat/index.js`: own messages right-aligned/primary, others
 * left-aligned/gray, sender name shown only on the first bubble of a
 * consecutive run, "Edited" tag, and a hover action menu standing in for
 * mobile's long-press bottom sheet.
 *
 * image/video/pdf messages decode the same way mobile's bubble-chat does —
 * see parseCustomMessage in chatHelpers.js. The "Inform"/push_button message
 * and private 1:1 chat remain the only deferred Phase 2 scope.
 *
 * `canModerate`/`onApprove`/`onReject` back the media-approval gate's inline
 * review path — a moderator's own preview of someone else's still-Pending
 * photo/video (ChatContext.loadPendingMessages) renders blurred with
 * Approve/Reject right on the bubble instead of the normal content, so
 * acting on it doesn't require opening the separate Pending Approval modal.
 * The "trust this sender" checkbox mirrors PendingApprovalModal's — both
 * ultimately call ChatContext.approveMessage/rejectMessage's same
 * applyToFutureUploads flag, so trusting inline behaves identically to
 * trusting via the modal.
 */
const MessageBubble = ({ message, isOwn, showSenderName, onEdit, onDelete, onShowInfo, canModerate, onApprove, onReject }) => {
  const { t } = useTranslation("chat");
  const [menuOpen, setMenuOpen] = useState(false);
  const [acting, setActing] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [trustFuture, setTrustFuture] = useState(false);
  const menuRef = useRef(null);
  const messageType = message.messageType;
  const isMedia = messageType === "image" || messageType === "video" || messageType === "pdf";
  // Only ever non-"Approved" on the sender's own bubble, or (with
  // moderationId set) a moderator's preview of someone else's — ChatHub.
  // SendMessage never broadcasts a Pending/Rejected message to anyone but
  // those two, and GetChatHistory filters it out room-wide otherwise (see
  // chatHelpers.js).
  const isPending = message.approvalStatus === "Pending";
  const isRejected = message.approvalStatus === "Rejected";
  const isModeratorPreview = canModerate && !isOwn && isPending && message.moderationId != null;

  const handleApprove = async () => {
    setActing(true);
    try {
      await onApprove(message, trustFuture);
    } catch (err) {
      toast.error(err.response?.data?.Message || t("pendingApproval.approveError"));
      setActing(false);
    }
  };

  const handleReject = async () => {
    setActing(true);
    try {
      await onReject(message, trustFuture);
    } catch (err) {
      toast.error(err.response?.data?.Message || t("pendingApproval.rejectError"));
      setActing(false);
    }
  };

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpen]);

  const handleCopy = () => {
    setMenuOpen(false);
    navigator.clipboard?.writeText(message.text || "");
    toast.success(t("bubble.copied"));
  };

  const renderContent = () => {
    if (messageType === "image") {
      return (
        <a href={message.text} target="_blank" rel="noreferrer" title={t("bubble.openImage")}>
          <img src={message.text} alt="" style={s.mediaImage} />
        </a>
      );
    }
    if (messageType === "video") {
      const { video_url: videoUrl, video_thumbnail: poster } = parseCustomMessage(message.text);
      return (
        <video
          src={videoUrl || message.text}
          poster={poster && poster !== (videoUrl || message.text) ? poster : undefined}
          controls
          style={s.mediaVideo}
        />
      );
    }
    if (messageType === "pdf") {
      const { file_url: fileUrl, name, size } = parseCustomMessage(message.text);
      const sizeMb = size ? (Number(size) / (1024 * 1024)).toFixed(2) : null;
      return (
        <a href={fileUrl || message.text} target="_blank" rel="noreferrer" style={{ ...s.pdfCard, color: isOwn ? "#fff" : color.textPrimary }}>
          <FileText size={22} strokeWidth={2} />
          <span style={s.pdfInfo}>
            <span style={s.pdfName}>{name || t("bubble.pdfFile")}</span>
            {sizeMb && <span style={s.pdfSize}>{sizeMb} MB</span>}
          </span>
          <Download size={15} strokeWidth={2} />
        </a>
      );
    }
    return <span style={s.text}>{message.text}</span>;
  };

  if (isModeratorPreview) {
    const { video_url: videoUrl } = messageType === "video" ? parseCustomMessage(message.text) : {};
    const mediaSrc = messageType === "video" ? (videoUrl || message.text) : message.text;
    return (
      <div style={{ ...s.row, justifyContent: "flex-start" }}>
        <div style={s.col}>
          {showSenderName && <span style={s.senderName}>{message.senderName}</span>}
          <div style={{ ...s.bubble, ...s.bubbleOther, ...s.bubbleMedia, ...s.previewBubble }}>
            <div style={s.previewWrap}>
              {messageType === "video" ? (
                <video
                  src={mediaSrc}
                  muted={!revealed}
                  controls={revealed}
                  style={{ ...s.previewMedia, ...(revealed ? {} : s.blurred) }}
                />
              ) : revealed ? (
                <a href={mediaSrc} target="_blank" rel="noreferrer" title={t("bubble.openImage")}>
                  <img src={mediaSrc} alt="" style={s.previewMedia} />
                </a>
              ) : (
                <img src={mediaSrc} alt="" style={{ ...s.previewMedia, ...s.blurred }} />
              )}

              <button
                type="button"
                style={s.viewToggleBtn}
                onClick={() => setRevealed((v) => !v)}
                aria-label={revealed ? t("bubble.hidePreview") : t("bubble.viewPreview")}
                title={revealed ? t("bubble.hidePreview") : t("bubble.viewPreview")}
              >
                {revealed ? <EyeOff size={14} strokeWidth={2.25} /> : <Eye size={14} strokeWidth={2.25} />}
              </button>

              {!revealed && <span style={s.previewTag}>{t("bubble.needsApproval")}</span>}
            </div>

            <label style={s.previewTrustRow}>
              <Checkbox checked={trustFuture} onChange={(e) => setTrustFuture(e.target.checked)} aria-label={t("pendingApproval.trustFuture")} />
              <span>{t("pendingApproval.trustFuture")}</span>
            </label>

            <div style={s.previewActionsRow}>
              <button type="button" style={s.previewApproveBtn} disabled={acting} onClick={handleApprove}>
                <Check size={13} strokeWidth={2.5} /> {t("pendingApproval.approve")}
              </button>
              <button type="button" style={s.previewRejectBtn} disabled={acting} onClick={handleReject}>
                <X size={13} strokeWidth={2.5} /> {t("pendingApproval.reject")}
              </button>
            </div>

            <div style={s.footer}>
              <span style={{ ...s.time, color: color.textMuted }}>
                {new Date(message.createdAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...s.row, justifyContent: isOwn ? "flex-end" : "flex-start" }}>
      <div style={s.col}>
        {showSenderName && !isOwn && <span style={s.senderName}>{message.senderName}</span>}

        <div
          style={{ ...s.bubble, ...(isOwn ? s.bubbleOwn : s.bubbleOther), ...(isMedia ? s.bubbleMedia : {}) }}
          onMouseEnter={() => setMenuOpen((v) => v)}
        >
          {renderContent()}
          {isPending && (
            <div style={s.approvalNote}>{t("bubble.pendingApproval")}</div>
          )}
          {isRejected && (
            <div style={{ ...s.approvalNote, ...s.approvalNoteRejected }}>
              {message.rejectionReason ? t("bubble.rejectedWithReason", { reason: message.rejectionReason }) : t("bubble.rejected")}
            </div>
          )}
          {message.isEdited && <span style={{ ...s.editedTag, color: isOwn ? "rgba(255,255,255,0.7)" : color.textMuted }}>{t("bubble.edited")}</span>}

          <div style={s.footer}>
            <span style={{ ...s.time, color: isOwn ? "rgba(255,255,255,0.7)" : color.textMuted }}>
              {new Date(message.createdAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
            </span>
            {isOwn && <MessageTicks message={message} />}
          </div>

          <div ref={menuRef} style={{ ...s.menuWrap, ...(isOwn ? { left: -30 } : { right: -30 }) }}>
            <button type="button" style={s.menuTrigger} onClick={() => setMenuOpen((v) => !v)} aria-label={t("bubble.info")}>
              <MoreVertical size={13} strokeWidth={2} color={color.textMuted} />
            </button>
            {menuOpen && (
              <div className="modal-pop" style={{ ...s.menu, ...(isOwn ? { right: 0 } : { left: 0 }) }}>
                {isOwn && !isMedia && (
                  <button className="row-link" style={s.menuItem} onClick={() => { setMenuOpen(false); onEdit(message); }}>
                    <Pencil size={13} strokeWidth={2} /> {t("bubble.edit")}
                  </button>
                )}
                <button className="row-link" style={s.menuItem} onClick={() => { setMenuOpen(false); onShowInfo(message); }}>
                  <Info size={13} strokeWidth={2} /> {t("bubble.info")}
                </button>
                {message.text && !isMedia && (
                  <button className="row-link" style={s.menuItem} onClick={handleCopy}>
                    <Copy size={13} strokeWidth={2} /> {t("bubble.copy")}
                  </button>
                )}
                <button className="row-link" style={{ ...s.menuItem, color: color.danger500 }} onClick={() => { setMenuOpen(false); onDelete(message); }}>
                  <Trash2 size={13} strokeWidth={2} /> {t("bubble.delete")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const s = {
  row: { display: "flex", width: "100%", marginBottom: 3 },
  col: { display: "flex", flexDirection: "column", maxWidth: "62%" },
  senderName: { fontSize: 11, fontWeight: 700, color: color.primary600, marginBottom: 3, marginLeft: 12 },
  bubble: { position: "relative", padding: "8px 12px 6px", borderRadius: radius.md },
  bubbleOwn: { background: color.primary500, color: "#fff", borderBottomRightRadius: 4 },
  bubbleOther: { background: color.gray100, color: color.textPrimary, borderBottomLeftRadius: 4 },
  bubbleMedia: { padding: 6 },
  text: { fontSize: 13.5, lineHeight: 1.45, whiteSpace: "pre-wrap", wordBreak: "break-word" },
  approvalNote: {
    fontSize: 10.5, fontWeight: 700, padding: "3px 6px", marginTop: 4, borderRadius: 4,
    background: "rgba(255,255,255,0.22)", color: "inherit", display: "inline-block",
  },
  approvalNoteRejected: { background: color.danger500, color: "#fff" },
  mediaImage: { display: "block", maxWidth: 220, maxHeight: 220, width: "100%", height: "auto", borderRadius: radius.sm, objectFit: "cover", cursor: "pointer" },
  mediaVideo: { display: "block", maxWidth: 220, maxHeight: 220, borderRadius: radius.sm, background: "#000" },
  previewBubble: { minWidth: 200 },
  previewWrap: { position: "relative", borderRadius: radius.sm, overflow: "hidden", background: color.gray100 },
  previewMedia: { display: "block", maxWidth: 240, maxHeight: 240, width: "100%", height: "auto", borderRadius: radius.sm, objectFit: "cover" },
  blurred: { filter: "blur(16px)", transform: "scale(1.08)", cursor: "default" },
  previewTag: {
    position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
    fontSize: 11, fontWeight: 700, color: "#fff", background: "rgba(17,18,38,0.68)",
    padding: "5px 12px", borderRadius: 999, letterSpacing: 0.2, whiteSpace: "nowrap",
    boxShadow: "0 2px 8px rgba(0,0,0,0.18)", pointerEvents: "none",
  },
  viewToggleBtn: {
    position: "absolute", top: 7, right: 7, width: 27, height: 27, borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(17,18,38,0.6)",
    border: "none", color: "#fff", cursor: "pointer", transition: "background 0.15s ease",
  },
  previewTrustRow: {
    display: "flex", alignItems: "center", gap: 6, fontSize: 10.5, color: color.gray700,
    cursor: "pointer", marginTop: 7, lineHeight: 1.3,
  },
  previewActionsRow: { display: "flex", gap: 6, marginTop: 6 },
  previewApproveBtn: {
    flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 5, fontSize: 11.5, fontWeight: 700, color: "#fff",
    background: color.primary500, border: "none", borderRadius: radius.sm, padding: "6px 8px", cursor: "pointer",
  },
  previewRejectBtn: {
    flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 5, fontSize: 11.5, fontWeight: 700, color: "#fff",
    background: color.danger500, border: "none", borderRadius: radius.sm, padding: "6px 8px", cursor: "pointer",
  },
  pdfCard: { display: "flex", alignItems: "center", gap: 8, padding: "6px 4px", textDecoration: "none", minWidth: 160 },
  pdfInfo: { display: "flex", flexDirection: "column", flex: 1, minWidth: 0 },
  pdfName: { fontSize: 12.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  pdfSize: { fontSize: 10.5, opacity: 0.75 },
  editedTag: { display: "block", fontSize: 10, fontStyle: "italic", marginTop: 2 },
  footer: { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4, marginTop: 3 },
  time: { fontSize: 10 },
  menuWrap: { position: "absolute", top: 4 },
  menuTrigger: { background: "none", border: "none", padding: 2, cursor: "pointer", opacity: 0.7, display: "flex" },
  menu: {
    position: "absolute", top: "calc(100% + 4px)", width: 150, background: color.surface, borderRadius: radius.md,
    boxShadow: "0 8px 24px rgba(20,21,40,0.16)", border: `1px solid ${color.border}`, overflow: "hidden", zIndex: 50, padding: 4,
  },
  menuItem: {
    width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: "transparent",
    border: "none", fontSize: 12.5, color: color.gray700, fontWeight: 500, cursor: "pointer", textAlign: "left", borderRadius: radius.sm,
  },
};

export default MessageBubble;
